import type { Database } from '@areapif/areapif-shared'

import { supabase } from '../lib/supabase'

type InspectionPhoto = Database['public']['CompositeTypes']['inspection_photo']
type InspectionSeal = Database['public']['CompositeTypes']['inspection_seal']

export type { InspectionPhoto, InspectionSeal }

/**
 * Upload a single file to the 'inspections' storage bucket.
 * The storage path is `{inspectionId}/{storageName}` where storageName
 * is provided by the caller (domain layer responsibility).
 * Returns the resulting storage path on success.
 */
export const uploadFile = async (inspectionId: string, file: File, storageName: string): Promise<string> => {
  const path = `${inspectionId}/${storageName}`
  const { data, error } = await supabase.storage.from('inspections').upload(path, file)

  if (error) {
    throw new Error(`Failed to upload file "${file.name}": ${error.message}`)
  }

  return data.path
}

/**
 * Remove files from the 'inspections' storage bucket.
 * Used for rollback when a partial upload fails.
 */
export const removeFiles = async (paths: string[]): Promise<void> => {
  if (paths.length === 0) return
  await supabase.storage.from('inspections').remove(paths)
}

/**
 * Fetch the current photos array from a historical inspection record.
 */
export const fetchExistingPhotos = async (inspectionId: string): Promise<InspectionPhoto[]> => {
  const { data, error } = await supabase
    .from('historical_inspections')
    .select('photos')
    .eq('id', inspectionId)
    .eq('is_deleted', false)
    .single()

  if (error) {
    throw new Error(`Failed to fetch inspection record: ${error.message}`)
  }

  return data.photos
}

/**
 * Fetch the current seals array from a historical inspection record.
 */
export const fetchExistingSeals = async (inspectionId: string): Promise<InspectionSeal[]> => {
  const { data, error } = await supabase
    .from('historical_inspections')
    .select('seals')
    .eq('id', inspectionId)
    .eq('is_deleted', false)
    .single()

  if (error) {
    throw new Error(`Failed to fetch inspection record: ${error.message}`)
  }

  return data.seals
}

/**
 * Write merged photos array back to a historical inspection record.
 * Throws if no rows are affected (indicates an RLS policy violation).
 *
 * Note: The read-then-update pattern is not atomic. Concurrent uploads to the
 * same inspection could cause data loss. For this backoffice tool with low
 * concurrency, this is an acceptable trade-off. A server-side Postgres function
 * using array_cat would be the robust solution.
 */
export const updatePhotos = async (inspectionId: string, photos: InspectionPhoto[]): Promise<void> => {
  const { data: updateData, error } = await supabase
    .from('historical_inspections')
    .update({ photos })
    .eq('id', inspectionId)
    .eq('is_deleted', false)
    .select('id')

  if (error) {
    throw new Error(`Failed to update inspection record: ${error.message}`)
  }

  if (updateData.length === 0) {
    throw new Error('Update failed: no rows were affected. Check RLS policies on historical_inspections.')
  }
}

/**
 * Write merged seals array back to a historical inspection record.
 * Throws if no rows are affected (indicates an RLS policy violation).
 *
 * Note: The read-then-update pattern is not atomic. Concurrent uploads to the
 * same inspection could cause data loss. For this backoffice tool with low
 * concurrency, this is an acceptable trade-off. A server-side Postgres function
 * using array_cat would be the robust solution.
 */
export const updateSeals = async (inspectionId: string, seals: InspectionSeal[]): Promise<void> => {
  const { data: updateData, error } = await supabase
    .from('historical_inspections')
    .update({ seals })
    .eq('id', inspectionId)
    .eq('is_deleted', false)
    .select('id')

  if (error) {
    throw new Error(`Failed to update inspection record: ${error.message}`)
  }

  if (updateData.length === 0) {
    throw new Error('Update failed: no rows were affected. Check RLS policies on historical_inspections.')
  }
}
